import React, { useRef, useCallback } from "react";
import type { ComponentProps } from "react";

import { Checkbox, HStack, Text, VStack } from "native-base";
import { Box } from "native-base";
import { FlatList } from "react-native";

import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import SelectRow from "pianofunclub-shared/components/ListItems/SelectRow";
import { pushOrRemove } from "pianofunclub-shared/utils/helpers";

type BoxProps = ComponentProps<typeof Box>;

interface Props extends BoxProps {
  data: { label: string; value: string }[];
  finishButtonText: string;
  onPressFinishButton: () => void;
  selectedValues: string[];
  setSelectedValues: (selectedValues: string[]) => void;
}

const MultiSelect = (props: Props) => {
  const {
    data,
    finishButtonText = "Done",
    onPressFinishButton,
    selectedValues,
    setSelectedValues,
  } = props;

  const rowRefs = useRef(new Map());

  const rowPressHandler = useCallback(
    (variables: { id: string }) => {
      setSelectedValues(pushOrRemove(selectedValues, variables.id));
    },
    [selectedValues, setSelectedValues]
  );

  const renderItem = useCallback(
    ({ item }: { item: { label: string; value: string } }) => {
      return (
        <SelectRow
          ref={(ref) => {
            if (ref && !rowRefs.current.get(item.value)) {
              rowRefs.current.set(item.value, ref);
            }
          }}
          id={item.value}
          initiallySelected={selectedValues.indexOf(item.value) !== -1}
          label={item.label}
          onSelect={rowPressHandler}
        />
      );
    },
    [rowPressHandler, selectedValues]
  );

  return (
    <>
      <VStack alignItems="center" flex={1} width="85%" {...props}>
        <Box
          bg="surface.100"
          borderColor="surface.200"
          borderRadius="2xl"
          borderWidth={1}
          height="380px"
          overflow="hidden"
          width="100%"
        >
          <HStack
            alignItems="center"
            bg="primary.50"
            borderBottomWidth={1}
            borderColor="surface.200"
            pb="2"
            pt="3"
            px="4"
            space="3"
          >
            <Checkbox
              isChecked={
                data.length <= selectedValues.length &&
                data.every((item) => selectedValues.includes(item.value))
              }
              onChange={(isSelected) => {
                rowRefs.current.forEach((ref) => {
                  ref.setIsChecked(isSelected);
                });
                setSelectedValues(isSelected ? data.map((item) => item.value) : []);
              }}
              value="SELECT_ALL"
            />
            <Text fontSize="md">Select All</Text>
          </HStack>
          <FlatList
            data={data}
            keyExtractor={(item) => item.value + "-" + selectedValues.indexOf(item.value)}
            renderItem={renderItem}
          />
        </Box>
      </VStack>
      <ButtonDebounced
        _text={{ fontSize: "xl" }}
        height="56px"
        isDisabled={selectedValues.length === 0}
        mb="4"
        mt="8"
        onPress={() => {
          rowRefs.current = new Map();
          onPressFinishButton();
        }}
        px="6"
      >
        {finishButtonText}
      </ButtonDebounced>
    </>
  );
};

export default MultiSelect;
